import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import { getProposedPageUpdate } from '../components/PageUpdateApprovalCard';
import { FORM_UI_REASONS } from './constants';
import type { FormUiFieldIssue, FormUiFields } from './types';
import { useUiContext } from './uiContext';
import {
  describeFormUiField,
  findFormUiField,
  flattenFormErrors,
  getFormUiFieldEntries,
  getFormUiReadDescription,
  getFormUiWriteDescription,
  getInvalidOptionReason,
  isFormUiFieldVisible,
  isMissingRequiredValue,
  previewFormUiUpdate,
} from './utils';

type Props<TForm extends FieldValues> = {
  name: string;
  prompt: string;
  methods: UseFormReturn<TForm>;
  isEnabled: boolean;
  fields: FormUiFields<TForm>;
  getState?: () => Record<string, unknown>;
  getUnavailableReason?: () => string | undefined;
};

const useFormUiContext = <TForm extends FieldValues>({
  name,
  prompt,
  methods,
  isEnabled,
  fields,
  getState,
  getUnavailableReason,
}: Props<TForm>) => {
  const readForm = () => {
    const values = methods.getValues();
    const visibleFields = getFormUiFieldEntries(fields).filter(([, field]) =>
      isFormUiFieldVisible(field, values),
    );
    return {
      form: Object.fromEntries(
        visibleFields
          .filter(([, field]) => !field.secret)
          .map(([fieldName]) => [
            fieldName,
            methods.getValues(fieldName) ?? null,
          ]),
      ),
      fields: visibleFields.map(([fieldName, field]) =>
        describeFormUiField(fieldName, field, methods.getValues(fieldName)),
      ),
      missingRequiredFields: visibleFields
        .filter(([fieldName, field]) =>
          isMissingRequiredValue(field, methods.getValues(fieldName)),
        )
        .map(([fieldName]) => fieldName),
      validationErrors: visibleFields.reduce<Record<string, string>>(
        (errors, [fieldName]) => ({
          ...errors,
          ...flattenFormErrors(
            methods.getFieldState(fieldName).error,
            fieldName,
          ),
        }),
        {},
      ),
      ...getState?.(),
    };
  };

  const rejectWrite = (reason: string) => ({
    applied: [],
    rejected: [{ field: 'form', reason }],
    ...readForm(),
  });

  useUiContext(
    isEnabled
      ? {
          [`read_${name}`]: {
            description: getFormUiReadDescription(name, prompt),
            handler: () => {
              const unavailableReason = getUnavailableReason?.();
              return unavailableReason
                ? { unavailable: unavailableReason }
                : readForm();
            },
          },
          [`write_${name}`]: {
            description: getFormUiWriteDescription(name, prompt),
            requiresApproval: true,
            enabledModes: ['agent'],
            preview: (proposed) => previewFormUiUpdate(fields, proposed),
            argsSchema: {
              type: 'object',
              properties: {
                form: { type: ['object', 'string'] },
                reason: { type: 'string' },
              },
            },
            handler: async (args) => {
              const unavailableReason = getUnavailableReason?.();
              if (unavailableReason) {
                return rejectWrite(unavailableReason);
              }
              const proposed = getProposedPageUpdate(args);
              if (!proposed) {
                return rejectWrite(FORM_UI_REASONS.invalidForm);
              }

              const rejected: FormUiFieldIssue[] = [];
              const accepted: Record<string, unknown> = {};
              Object.entries(proposed).forEach(([fieldName, value]) => {
                const field = findFormUiField(fields, fieldName);
                if (!field) {
                  rejected.push({
                    field: fieldName,
                    reason: FORM_UI_REASONS.unknownField,
                  });
                  return;
                }
                if (field.secret) {
                  rejected.push({
                    field: fieldName,
                    reason: FORM_UI_REASONS.userOnlyField,
                  });
                  return;
                }
                const invalidOptionReason = getInvalidOptionReason(
                  field.options,
                  value,
                );
                if (invalidOptionReason) {
                  rejected.push({
                    field: fieldName,
                    reason: invalidOptionReason,
                  });
                  return;
                }
                accepted[fieldName] = value;
              });

              const nextValues = { ...methods.getValues(), ...accepted };
              const applied: Path<TForm>[] = [];
              const changedByHandlers = new Set<Path<TForm>>();
              const handlerMethods: UseFormReturn<TForm> = {
                ...methods,
                setValue: (name, value, options) => {
                  changedByHandlers.add(name);
                  methods.setValue(name, value, options);
                },
              };
              getFormUiFieldEntries(fields).forEach(([fieldName, field]) => {
                if (!(fieldName in accepted)) {
                  return;
                }
                if (!isFormUiFieldVisible(field, nextValues)) {
                  rejected.push({
                    field: fieldName,
                    reason: FORM_UI_REASONS.hiddenField,
                  });
                  return;
                }
                const value = accepted[fieldName];
                if (field.onWrite) {
                  const result = field.onWrite(value, handlerMethods);
                  if (result.ok) {
                    applied.push(fieldName);
                  } else {
                    rejected.push({ field: fieldName, reason: result.reason });
                  }
                  return;
                }
                methods.setValue(
                  fieldName,
                  value as PathValue<TForm, Path<TForm>>,
                  { shouldDirty: true },
                );
                applied.push(fieldName);
              });

              const changed = [...new Set([...applied, ...changedByHandlers])];
              if (changed.length > 0) {
                await methods.trigger(changed);
              }
              return { applied, rejected, ...readForm() };
            },
          },
        }
      : {},
  );
};

export default useFormUiContext;
